<template>
	<div class="w-full panel-wrapper">
		<ClientOnly>
			<LoadingSpinner
				v-if="loading"
				class="w-full h-56 p-8 dark:text-mx-gray-400"
			/>
			<div
				v-else
				class="flex flex-col w-full space-y-6 lg:grid lg:grid-cols-4 lg:gap-x-6 dark:text-white"
			>
				<!-- Account Selector -->
				<AccountDashboardAccountSelector
					v-if="showAccountSelector"
					v-model="viewableAccount"
					:account-options="accountOptions"
					:full-name="fullName"
					:account-id="accountId"
					:formatted-address="formatAddress(selectedAccount?.address)"
				/>
				<div
					v-else
					class="lg:col-span-4 flex justify-between"
				>
					<div class="flex flex-wrap items-center">
						<span class="text-3xl pr-2">
							{{ t('dashboard.welcome', { fullName }) }}
						</span>
						<span class="text-2xl md:text-nowrap">
							{{ t('dashboard.subtitle') }}
						</span>
					</div>
					<div class="hidden md:block">
						<LinkButton
							to="add-address"
							:text="t('dashboard.add-address')"
							class="uppercase h-9 min-w-56"
						/>
					</div>
				</div>

				<!-- Next Payment Due CTA -->
				<AccountDashboardNextPaymentCta
					v-if="hasNextPayment"
					:has-auto-pay="hasAutoPay"
					:next-payment-due-text="nextPaymentDueText"
					:autopay-method-text="autopayMethodText"
					:next-payment-invoice-id="nextPaymentInvoiceId"
				/>

				<!-- Overdue Balance / Due Today -->
				<AccountDashboardOverdueBalance
					:has-overdue="hasOverdue"
					:overdue-balance="overdueBalance"
					:total-overdue-amount="totalOverdueAmount"
				/>

				<!-- Next Billing Info -->
				<AccountDashboardNextBillingInfo
					:next-billing-cycle-amount="nextBillingCycleAmount"
					:msdy-date="msdyDate"
				/>

				<!-- Enable Autopay CTA -->
				<AccountDashboardAutopayCta v-if="showAutopayCta" />

				<AccountDashboardCurrentInvoice
					v-if="hasNextPayment"
					class="col-span-4 lg:col-span-2"
					:invoice="nextPaymentDue"
				/>
				<AccountDashboardPaymentMethods
					class="col-span-4 lg:col-span-2"
					:payment-methods="paymentMethods"
				/>
				<AccountDashboardServices
					v-if="accountServices"
					class="col-span-4 lg:col-span-2"
					:account-services="accountServices"
					:voip-numbers-count="voipNumbersCount"
					:static-ips-count="staticIpsCount"
				/>
				<AccountDashboardMyEquipment />
			</div>
		</ClientOnly>
	</div>
</template>

<script setup lang="ts">
import { useAuth } from '@/composables/useAuth'
import { useInvoices } from '@/composables/useInvoices'
import { usePaymentMethods } from '@/composables/usePaymentMethods'
import { BANK_ACCOUNT } from '@/constants/payment'
import { formatDate, MSDY } from '@/utils/formatDate'
import { toCurrencyString } from '@/utils/formatNumbers'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const {
	getInvoices, nextPaymentDueDate, hasNextPayment, nextPaymentDueAmount, totalOverdueAmount, getPastDue, nextPaymentInvoiceId, loading, nextPaymentDue, hasOverdue
} = useInvoices()
const {
	getPaymentMethods, hasAutoPay, autopayPaymentMethod, autoPayLastFourDigits, paymentMethods
} = usePaymentMethods()
const {
	accountId, autopayDiscountEligible, hasMultipleAccounts, selectedAccount, fullName, accounts, setSelectedAccount, formatAddress, accountServices, voipNumbersCount, staticIpsCount
} = useAuth()

const viewableAccount = ref({
	id: '',
	name: ''
})

watch(accountId, async () => {
	if (!accountId.value) {
		return
	}
	await getInvoices({ limit: 24 })
	await getPastDue({ limit: 24 })
	await getPaymentMethods()
}, { immediate: true })

watch(viewableAccount, newAccount => {
	if (newAccount.id) {
		setSelectedAccount(parseInt(newAccount.id))
	}
})

const showAccountSelector = computed(() => hasMultipleAccounts.value && accountOptions?.value.length)
const showAutopayCta = computed(() => !hasAutoPay.value && autopayDiscountEligible.value)
const nextBillingCycleAmount = computed(() => toCurrencyString(nextPaymentDueAmount.value))
const msdyDate = computed(() => formatDate(nextPaymentDueDate?.value, MSDY) || 'N/A')

const overdueBalance = computed(() => {
	return toCurrencyString(totalOverdueAmount.value)
})

const accountOptions = computed(() => {
	return accounts?.value.map(account => {
		return {
			id: account.accountId,
			name: formatAddress(account.address)
		}
	})
})

const nextPaymentDueText = computed(() => {
	const dueDate = nextPaymentDueDate.value || 'N/A'
	const amount = nextBillingCycleAmount.value || 'N/A'
	return hasAutoPay.value
		? t('account-page.next-payment.autopay', {
			dueDate,
			amount
		})
		: t('account-page.next-payment.payment', {
			dueDate,
			amount
		})
})

const autopayMethodText = computed(() => {
	return autopayPaymentMethod.value?.type === BANK_ACCOUNT
		? t('account-page.autopay.bank-account', { lastFour: autoPayLastFourDigits.value })
		: t('account-page.autopay.credit-card', { lastFour: autoPayLastFourDigits.value })
})
</script>
